import React, { Suspense, lazy, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import { SiteContentProvider } from './content/SiteContentProvider';
import { ThemeProvider } from './theme/ThemeProvider';

const Home = lazy(() => import('./pages/Home'));
const Explore = lazy(() => import('./pages/Explore'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Curriculum = lazy(() => import('./pages/Curriculum'));
const AcceleratorProgram = lazy(() => import('./pages/AcceleratorProgram'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Contact = lazy(() => import('./pages/Contact'));
const Gallery = lazy(() => import('./pages/Gallery'));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
const ProgramDetail = lazy(() => import('./pages/ProgramDetail'));
const Enrollment = lazy(() => import('./pages/Enrollment'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const StaffPlanner = lazy(() => import('./pages/StaffPlanner'));
const ChatBot = lazy(() => import('./components/ChatBot'));

function RouteFallback() {
  return (
    <div className="flex min-h-[45vh] items-center justify-center px-4 py-20 text-center text-slate-300">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-300">Loading</p>
        <p className="mt-3 text-lg">Preparing the next page...</p>
      </div>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const hideChatBot = ['/admin', '/staff', '/planner'].includes(location.pathname);

  useEffect(() => {
    const widgets = document.querySelectorAll<HTMLElement>('iframe[src*="jotform"], iframe[src*="jotfor"], [class*="ai-agent"], [id*="jotform"], [id*="Jotform"]');
    if (!widgets.length) return;
    if (!hideChatBot) {
      widgets.forEach(element => {
        element.style.removeProperty('display');
        element.style.removeProperty('visibility');
        element.style.removeProperty('pointer-events');
      });
      return;
    }
    document.querySelectorAll('iframe[src*="jotform"], iframe[src*="jotfor"], [class*="ai-agent"], [id*="jotform"], [id*="Jotform"]')
      .forEach(element => {
        const node = element as HTMLElement;
        node.style.display = 'none';
        node.style.visibility = 'hidden';
        node.style.pointerEvents = 'none';
      });
  }, [hideChatBot, location.pathname]);

  return (
    <div className="app-shell flex min-h-screen flex-col bg-[#020817] transition-colors duration-300">
      <PerformanceOptimizer />
      <Routes>
        <Route path="/*" element={
          <>
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/explore" element={<Explore />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/programs/:courseId" element={<ProgramDetail />} />
                  <Route path="/curriculum" element={<Curriculum />} />
                  <Route path="/accelerator" element={<AcceleratorProgram />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/blog" element={<Navigate to="/" replace />} />
                  <Route path="/blog/:id" element={<Navigate to="/" replace />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/email-templates" element={<EmailTemplates />} />
                  <Route path="/enroll" element={<Enrollment />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/staff" element={<StaffPlanner />} />
                  <Route path="/planner" element={<StaffPlanner />} />
                </Routes>
              </Suspense>
            </main>
            {!hideChatBot && (
              <Suspense fallback={null}>
                <ChatBot />
              </Suspense>
            )}
          </>
        } />
      </Routes>
    </div>
  );
}

function AppRouter() {
  return (
    <HashRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AppShell />
    </HashRouter>
  );
}

function App() {
  return (
    <ThemeProvider>
      <SiteContentProvider>
        <AppRouter />
      </SiteContentProvider>
    </ThemeProvider>
  );
}

export default App;
